import React, { useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { Grid, makeStyles, Typography } from "@material-ui/core";
import styles from "../styles";
import { get, set } from "@totalsoft/react-state-lens";
import { REMOVE_REQUIRED_DOCUMENT, UPLOAD_DEALER_DOCUMENT } from "../../dataCompletion/mutations";
import { DOWNLOAD_DMS_FILE_QUERY } from "features/dataCompletion/queries";
import { useClientQueryWithErrorHandling } from "hooks/errorHandling";
import fileSaver from "file-saver";
import { convertBase64ToFile } from "utils/fileConverter";
import { useMutationWithErrorHandling } from "hooks/errorHandling";
import UploadDocuments from "features/common/components/UploadDocuments";
import DownloadOrDeleteDocument from "features/common/components/DownloadOrDeleteDocument";
import { useTranslation } from "react-i18next";
import { addToast } from "components/notifications";
import { quoteSteps } from "features/quote/constants";
import { useApolloClient } from "@apollo/client";
import { gql } from "@apollo/client";

const useStyles = makeStyles(styles);

function DealerDocumentsContainer(props) {
  const { quoteId, index, dealerDocumentLens } = props;
  const classes = useStyles();
  const clientQuery = useClientQueryWithErrorHandling();
  const { t } = useTranslation();
  const client = useApolloClient();

  const enabledAddFile = useMemo(() => {
    if (!(dealerDocumentLens.file.sharePointUID |> get)) return true;
    return false;
  }, [dealerDocumentLens.file.sharePointUID]);

  const handleDownloadFile = useCallback(
    async (sharePointUID, fileName) => {
      const variables = { quoteId: parseInt(quoteId), sharePointUID, fileName, quoteStep: quoteSteps.contract };
      const { data } = await clientQuery(DOWNLOAD_DMS_FILE_QUERY, { variables, fetchPolicy: "no-cache" });

      if (!data) return;

      const fileURL = convertBase64ToFile(data.downloadDmsFile.content);
      fileSaver.saveAs(fileURL, data.downloadDmsFile.name);
    },
    [clientQuery, quoteId]
  );

  const [removeFile] = useMutationWithErrorHandling(REMOVE_REQUIRED_DOCUMENT);

  const handleDeleteFile = useCallback(async () => {
    const sharePointUID = dealerDocumentLens.file.sharePointUID |> get;
    const variables = { quoteId: parseInt(quoteId), sharePointUID };
    await removeFile({ variables });

    const idToDelete = client.cache.identify({
      __typename: "DmsFile",
      sharePointUID: sharePointUID,
    });

    client.cache.evict({ id: idToDelete });
    client.cache.gc();

    (dealerDocumentLens.file |> set)(null);
  }, [dealerDocumentLens, removeFile, quoteId, client.cache]);

  const [uploadFile] = useMutationWithErrorHandling(UPLOAD_DEALER_DOCUMENT, {
    onCompleted: (data) => {
      client.cache.modify({
        id: client.cache.identify({
          __typename: "DealerDocument",
          dmsDocumentId: dealerDocumentLens.dmsDocumentId |> get,
        }),
        fields: {
          file(fileRef) {
            fileRef = client.cache.writeFragment({
              data: data?.uploadDealerDocument[0],
              fragment: gql`
                fragment NewFile on DmsFile {
                  sharePointUID
                  fileName
                }
              `,
            });
            return fileRef;
          },
        },
      });

      (dealerDocumentLens.file |> set)(data?.uploadDealerDocument[0]);
    },
  });

  const handleUpload = useCallback(
    async (files) => {
      if (files.length <= 0) return false;

      const uploadedFiles = files.map((file) => {
        return file.file;
      });

      const documentId = dealerDocumentLens.dmsDocumentId |> get;
      const siteId = dealerDocumentLens.dmsSiteId |> get;
      const variables = {
        quoteId: parseInt(quoteId),
        documentId,
        siteId,
        uploadedFiles,
      };

      await uploadFile({ variables });
      addToast(t("ToastNotifications.Success"), "success");
    },
    [quoteId, uploadFile, dealerDocumentLens.dmsDocumentId, dealerDocumentLens.dmsSiteId, t]
  );

  return (
    <Grid container alignItems="center" style={{ backgroundColor: "white", padding: "10px", borderRadius: "6px" }}>
      <Grid item>
        <Typography variant="h6" className={classes.reqText}>
          {dealerDocumentLens.dmsContentType |> get}
        </Typography>
      </Grid>
      <Grid item xs>
        <UploadDocuments disabled={!enabledAddFile} onUpload={handleUpload} filesLimit={1} />
      </Grid>
      {(dealerDocumentLens.file?.fileName |> get) && (
        <DownloadOrDeleteDocument
          key={index}
          index={index}
          file={dealerDocumentLens.file |> get}
          onDownloadFile={handleDownloadFile}
          onDeleteFile={handleDeleteFile}
        />
      )}
    </Grid>
  );
}

DealerDocumentsContainer.propTypes = {
  quoteId: PropTypes.number,
  index: PropTypes.number.isRequired,
  dealerDocumentLens: PropTypes.object.isRequired,
};

export default DealerDocumentsContainer;
